import { mainRiverName, upstreamIndex, type MainName } from "@flowsight/sim";
import { graphRef } from "./sim-client";

/** Nearest named OSM waterway per HydroRIVERS reach (v0.6.4).
 *  Data: /data/names-by-reach.json  { "<reachId>": "<ชื่อ>" } — built offline from
 *  Geofabrik gis_osm_waterways_free_1 (© OpenStreetMap contributors, ODbL).
 *  Missing file ⇒ feature degrades silently (no name shown). */
let cache: Record<string, string> | null = null;
let pending: Promise<Record<string, string>> | null = null;

export async function loadRiverNames(): Promise<Record<string, string>> {
  if (cache) return cache;
  if (!pending) {
    pending = fetch("/data/names-by-reach.json")
      .then((r) => (r.ok ? (r.json() as Promise<Record<string, string>>) : {}))
      .then((m) => (cache = m ?? {}))
      .catch(() => (cache = {}));
  }
  return pending;
}

/** Name for a reach id if loaded and known; otherwise null. */
export function riverName(reachId: number | string): string | null {
  if (!cache) return null;
  const n = cache[String(reachId)];
  return n && n.trim() ? n : null;
}

/** v0.9.5b: the name people use for a MAIN river at this reach — the most common name (by
 *  length) along the continuous same-order stretch, with the reach's own label kept as
 *  "(ช่วง…)" when it differs (e.g. "แม่น้ำเจ้าพระยา (ช่วงลัดเกร็ด)"). Small streams keep their
 *  own name. Falls back to riverName() until the graph is loaded. */
let upIdx: Map<number, number[]> | null = null;
export function mainRiver(reachId: number): MainName | null {
  if (!cache) return null;
  const g = graphRef();
  if (!g) { const n = riverName(reachId); return { name: n, main: null, own: n, stretchKm: 0 }; }
  upIdx ??= upstreamIndex(g);
  return mainRiverName(g, cache, reachId, upIdx);
}
export function displayRiverName(reachId: number): string | null { return mainRiver(reachId)?.name ?? riverName(reachId); }

/** The river name as shown to people: "คลอง…/แม่น้ำ…/ลำ…/ห้วย…" kept as is, a bare name gets "แม่น้ำ".
 *  Shared by the summary row, the 〰 chip and the 📍 label (v0.9.5c). */
export function riverDisplayLabel(name: string): string { return /^(แม่น้ำ|คลอง|ลำ|ห้วย)/.test(name) ? name : `แม่น้ำ${name}`; }
